import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import CasesSummary from 'dashboard/components/CasesSummary';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import { ReportCustomListDetails } from 'dashboard/components/ReportCustomListDetails/ReportCustomListDetails';
import { ReportInputCustomList } from 'dashboard/components/ReportInput';
import TagsSummary from 'dashboard/components/TagsSummary';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import {
  Backdrop,
  ColumnContainer,
  Container,
  HeaderContainer,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Case } from 'dashboard/models/case';
import { RelatedReport, Report, REPORT_TYPE_TO_LABEL } from 'dashboard/models/report';
import { ReportCustomList } from 'dashboard/models/report/custom-list';
import { ReportRun, ReportRunCustomList } from 'dashboard/models/report-run';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import ReportInfo from 'dashboard/views/DashboardReportsShowView/ReportInfo';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { ApiModel } from 'lib/interfaces/api';

interface DashboardReportRunsShowViewProps {
  report: Report;
  reportRun?: ReportRun;
  reportTemplate: ReportTemplate;
  reportTags: Tag[];
  cases: Case[];
  users: User[];
  relatedReports: RelatedReport[];
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
}

const DashboardReportRunsShowView = ({
  report,
  reportRun,
  reportTemplate,
  reportTags,
  cases,
  users,
  relatedReports,
  handleUpdateTags,
}: DashboardReportRunsShowViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <Helmet title={`${reportRun.id} - Runs - ${report.id} - Reports`} />
      <InactiveEnvironmentBanner />
      <Backdrop>
        <Container>
          <HeaderContainer>
            <Header>
              <NavBackContainer>
                <StyledBackButton
                  id='reports__header-back-button'
                  onClick={() => {
                    // allow the user to go back if were redirected internally for best UX
                    if (location.state?.internalRedirect) {
                      navigate(-1);
                    } else {
                      navigate(
                        reportTemplate.id
                          ? `/dashboard/report-templates/${reportTemplate.id}`
                          : '/dashboard/report-templates',
                      );
                    }
                  }}
                  size='sm'
                  width='auto'
                  variant='basic'
                  prefixIcon={<ArrowLeftIcon />}
                >
                  {`${reportTemplate?.attributes.name} / ${
                    report?.attributes['primary-input'] ?? ''
                  }`}
                </StyledBackButton>
              </NavBackContainer>
            </Header>
          </HeaderContainer>
          <ColumnContainer>
            <MainColumn>
              {/* Only supports custom list for now  */}
              <Card>
                <Card.Body>
                  <Card.Title>Input</Card.Title>
                  <ReportInputCustomList
                    report={report as ReportCustomList}
                    isRedacted={report.attributes['redacted-at'] != null}
                  />
                </Card.Body>
              </Card>
              <ReportCustomListDetails
                report={report as ReportCustomList}
                reportRun={reportRun as ReportRunCustomList}
                multiCardFormat={true}
              />
            </MainColumn>
            <RightColumn>
              <ReportInfo report={report} reportRun={reportRun} reportTemplate={reportTemplate} />
              {relatedReports.length > 0 && (
                <Card>
                  <Card.Body>
                    <Card.Title>Related reports</Card.Title>
                    {relatedReports.map((report) => (
                      <Link to={`/dashboard/reports/${report.token}`} key={report.token}>{`${
                        REPORT_TYPE_TO_LABEL[report.type]
                      }`}</Link>
                    ))}
                  </Card.Body>
                </Card>
              )}
              <TagsSummary
                tags={reportTags}
                tagScope={ApiModel.Report}
                handleUpdateTags={handleUpdateTags}
              />
              <CasesSummary
                cases={cases}
                users={users}
                nullStateText='There are no cases associated with this report.'
              />
            </RightColumn>
          </ColumnContainer>
        </Container>
      </Backdrop>
    </>
  );
};

const Header = styled('div')`
  display: flex;
`;

const NavBackContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const StyledBackButton = styled(Button)`
  background: none;
  padding-left: 0;
`;

export default DashboardReportRunsShowView;
